// src/components/JobPicker.js
// Picks a jobsite (or other location) with suggestions on top (David, 10/29):
//   📍 You're here      — jobs within 400 ft of the phone (all of them)
//   🗓 Labor Board jobs — everything the person is on this week
//   📌 Nearby           — the next closest within 1/2 mile
// then a search box over the full list. At the shop the Labor Board jobs come
// first, since tools are checked out there before they go to a job.
//
// locations: the full list to choose from (already filtered to the right
//   types for the action). Suggestions outside it are left out.
// forWorker: who will have the tool ({ worker_id, display_name }), for the
//   Labor Board jobs. The person scanning, or a transfer's recipient.
// onPick(location, source): source is 'here' | 'nearby' | 'labor_board' |
//   'search' | 'list', saved with the transaction as job_pick_source.
import React, { useContext } from 'react';
import { AuthContext } from '../App';
import SearchPicker from './SearchPicker';
import useJobSuggestions from '../hooks/useJobSuggestions';

const distance = (ft) => (ft < 1000 ? `${ft} ft away` : `${(ft / 5280).toFixed(1)} mi away`);

export default function JobPicker({ locations, forWorker, onPick, selectedId = null, allTitle = 'All jobs and locations', placeholder }) {
  const { worker } = useContext(AuthContext);
  const s = useJobSuggestions(forWorker?.worker_id);

  const byId = new Map(locations.map((l) => [l.location_id, l]));
  const keep = (list) => (list || []).filter((l) => byId.has(l.location_id)).map((l) => ({ ...byId.get(l.location_id), ...l }));
  const here = keep(s.here), nearby = keep(s.nearby), assigned = keep(s.assigned);

  const someoneElse = forWorker && forWorker.worker_id !== worker?.worker_id;
  const whose = someoneElse ? `${(forWorker.display_name || 'Their').split(' ')[0]}'s` : 'Your';
  const hereGroup = { key: 'here', source: 'here', icon: '📍', title: "You're here", items: here, note: (l) => (l.distance_ft > 50 ? distance(l.distance_ft) : null) };
  const lbGroup = { key: 'lb', source: 'labor_board', icon: '🗓', title: `${whose} Labor Board jobs this week`, items: assigned, note: (l) => (l.week_hours ? `${l.week_hours} hrs this week` : null) };
  const nearGroup = { key: 'near', source: 'nearby', icon: '📌', title: 'Nearby', items: nearby, note: (l) => distance(l.distance_ft) };
  const groups = s.at_shop ? [lbGroup] : [hereGroup, lbGroup, nearGroup];

  const status = s.loading ? 'Finding jobs near you…'
    : !s.have_gps ? 'Location is off, so only Labor Board jobs are suggested. Search below for any job.'
    : s.at_shop ? "You're at the shop. Search below if the job isn't listed."
    : (!here.length && !nearby.length) ? "No jobs within ½ mile. Search below for any job."
    : null;

  return (
    <SearchPicker
      items={locations}
      groups={s.loading ? [] : groups}
      getKey={(l) => l.location_id}
      getLabel={(l) => l.name}
      getSub={(l) => [l.address, l.location_type !== 'jobsite' ? l.location_type : null].filter(Boolean).join(' · ')}
      icon="📍"
      onPick={onPick}
      selectedKey={selectedId}
      placeholder={placeholder || 'Search jobs by name or number…'}
      allTitle={allTitle}
      emptyText="No jobs found"
      status={status}
    />
  );
}
